import React from 'react';
import rehypeRaw from "rehype-raw";
import ReactMarkdown from "react-markdown";

import {
    sectionContent,
    intro,
    cmsContent,
    tagContent,
    heading,
    tagsContent,
    tag as tagClass,
    lookWrapper,
    lookImageWrap,
    lookImage,
    lookPoint,
    lookPointNumber,
    lookPointActive,
    lookPointCard,
    lookPointCardLabel,
    lookPointCardDesc,
    lookPointCardLink,
    lookPointOpen,
    lookLegend,
    lookLegendItem,
    lookLegendItemActive,
    lookLegendNumber,
    lookLegendLabel,
    lookMobileSheet,
    lookMobileSheetHeader,
    lookMobileSheetNumber,
    lookMobileSheetLabel,
    lookMobileSheetClose,
    lookMobileSheetDesc,
    lookMobileSheetLink,
    lookMessage,
} from './post-content.module.scss'
import { IPost } from "../../models/post.model";
import { useTranslation } from "../../hooks/use-translation";
import { config } from '../../config';

import Section from "../molecules/section";
import Tag from '../atoms/tag';

const { translationKeys } = config;

interface IPostContentProps {
    post: IPost,
}

interface ILookPoint {
    number: string;
    x: number;
    y: number;
    url: string;
    label?: string;
    description?: string;
}

interface ILookBlock {
    type: "look";
    image: string;
    imageAlt?: string;
    points: ILookPoint[];
}

interface ILookIdBlock {
    type: "look-id";
    lookId: number;
}

interface IMarkdownBlock {
    type: "markdown";
    content: string;
}

interface ILookApiResponse {
    lookId: number;
    name: string;
    imageUrl: string;
    imageAlt?: string;
    items: ILookPoint[];
}

type IContentBlock = ILookBlock | ILookIdBlock | IMarkdownBlock;

const lookBlockPattern = /\[LOOK\s+image=["']([^"']+)["']\]([\s\S]*?)\[\/LOOK\]|\[LOOK\s+id=["']?(\d+)["']?\s*\]/gi;

const parseLookPoints = (raw: string): ILookPoint[] =>
    raw
        .split(/\r?\n/)
        .map(line => line.trim())
        .filter(Boolean)
        .map(line => {
            const [number, x, y, url, label, description] = line.split("|").map(part => part.trim());

            return {
                number,
                x: Number(x),
                y: Number(y),
                url,
                label,
                description: description || undefined,
            };
        })
        .filter(point =>
            !!point.number &&
            !!point.url &&
            Number.isFinite(point.x) &&
            Number.isFinite(point.y)
        );

const parseContentBlocks = (content = ""): IContentBlock[] => {
    const blocks: IContentBlock[] = [];
    let lastIndex = 0;
    let match: RegExpExecArray | null;

    while ((match = lookBlockPattern.exec(content)) !== null) {
        const markdown = content.slice(lastIndex, match.index);

        if (markdown.trim()) {
            blocks.push({ type: "markdown", content: markdown });
        }

        const [, image, pointsRaw, lookIdRaw] = match;

        if (lookIdRaw) {
            blocks.push({ type: "look-id", lookId: Number(lookIdRaw) });
            lastIndex = match.index + match[0].length;
            continue;
        }

        const points = parseLookPoints(pointsRaw || "");

        if (image && points.length > 0) {
            blocks.push({ type: "look", image: image.trim(), points });
        }

        lastIndex = match.index + match[0].length;
    }

    const rest = content.slice(lastIndex);
    if (rest.trim()) {
        blocks.push({ type: "markdown", content: rest });
    }

    return blocks.length ? blocks : [{ type: "markdown", content }];
};

const AFFILIATE_REL = "nofollow sponsored noopener noreferrer";

// Zgodne z breakpointem "bigPhone" w styles/breakpoints.scss (widok mobilny).
const MOBILE_QUERY = "(max-width: 570px)";

const useIsMobile = (): boolean => {
    const [isMobile, setIsMobile] = React.useState(false);

    React.useEffect(() => {
        if (typeof window === "undefined" || !window.matchMedia) {
            return;
        }

        const mql = window.matchMedia(MOBILE_QUERY);
        const apply = () => setIsMobile(mql.matches);
        apply();
        mql.addEventListener("change", apply);
        return () => {
            mql.removeEventListener("change", apply);
        };
    }, []);

    return isMobile;
};

const LookPoint: React.FC<{
    point: ILookPoint;
    isMobile: boolean;
    active: boolean;
    onOpen: (point: ILookPoint) => void;
}> = ({ point, isMobile, active, onOpen }) => {
    const [open, setOpen] = React.useState(false);
    const rootRef = React.useRef<HTMLDivElement>(null);
    const label = point.label ? `${point.label}` : `Produkt ${point.number}`;
    const left = Math.max(0, Math.min(100, point.x));
    const top = Math.max(0, Math.min(100, point.y));

    React.useEffect(() => {
        if (!open || isMobile) {
            return;
        }

        const handleOutside = (event: PointerEvent) => {
            if (rootRef.current && !rootRef.current.contains(event.target as Node)) {
                setOpen(false);
            }
        };

        document.addEventListener("pointerdown", handleOutside);
        return () => {
            document.removeEventListener("pointerdown", handleOutside);
        };
    }, [open, isMobile]);

    const isTouchLike = typeof window !== "undefined" &&
        window.matchMedia &&
        window.matchMedia("(hover: none), (pointer: coarse)").matches;

    const handleNumberClick = (event: React.MouseEvent<HTMLAnchorElement>) => {
        if (isMobile) {
            event.preventDefault();
            onOpen(point);
            return;
        }

        if (isTouchLike) {
            event.preventDefault();
            setOpen(current => !current);
        }
    };

    const pointClasses = [
        lookPoint,
        active ? lookPointActive : "",
        !isMobile && open ? lookPointOpen : "",
    ].filter(Boolean).join(" ");

    return (
        <div
            ref={rootRef}
            className={pointClasses}
            style={{ left: `${left}%`, top: `${top}%` }}
        >
            <a
                className={lookPointNumber}
                href={point.url}
                target="_blank"
                rel={AFFILIATE_REL}
                aria-label={label}
                onClick={handleNumberClick}
            >
                {point.number}
            </a>
            {!isMobile && (
                <div className={lookPointCard} aria-hidden={!open}>
                    <span className={lookPointCardLabel}>{label}</span>
                    {point.description && (
                        <p className={lookPointCardDesc}>{point.description}</p>
                    )}
                    <a
                        className={lookPointCardLink}
                        href={point.url}
                        target="_blank"
                        rel={AFFILIATE_REL}
                    >
                        Zobacz produkt
                    </a>
                </div>
            )}
        </div>
    );
};

const LookHotspots: React.FC<ILookBlock> = ({ image, imageAlt, points }) => {
    const isMobile = useIsMobile();
    const [activePoint, setActivePoint] = React.useState<ILookPoint | null>(null);
    const figureRef = React.useRef<HTMLElement>(null);
    const imageRef = React.useRef<HTMLImageElement>(null);

    const togglePoint = React.useCallback((point: ILookPoint, scrollImage: boolean) => {
        setActivePoint(current => {
            const next = current === point ? null : point;

            if (next && scrollImage) {
                imageRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
            }

            return next;
        });
    }, []);

    // Zamknięcie arkusza po dotknięciu na zewnątrz (tylko widok mobilny).
    React.useEffect(() => {
        if (!isMobile || !activePoint) {
            return;
        }

        const handleOutside = (event: PointerEvent) => {
            if (figureRef.current && !figureRef.current.contains(event.target as Node)) {
                setActivePoint(null);
            }
        };

        document.addEventListener("pointerdown", handleOutside);
        return () => {
            document.removeEventListener("pointerdown", handleOutside);
        };
    }, [isMobile, activePoint]);

    // Zamknięcie arkusza klawiszem Esc + blokada scrolla pod arkuszem.
    React.useEffect(() => {
        if (!isMobile || !activePoint) {
            return;
        }

        const handleKey = (event: KeyboardEvent) => {
            if (event.key === "Escape") {
                setActivePoint(null);
            }
        };

        document.addEventListener("keydown", handleKey);
        const previousOverflow = document.body.style.overflow;
        document.body.style.overflow = "hidden";

        return () => {
            document.removeEventListener("keydown", handleKey);
            document.body.style.overflow = previousOverflow;
        };
    }, [isMobile, activePoint]);

    return (
        <figure className={lookWrapper} ref={figureRef}>
            <div className={lookImageWrap}>
                <img
                    ref={imageRef}
                    className={lookImage}
                    src={image}
                    alt={imageAlt || "Stylizacja z oznaczonymi produktami"}
                    loading="lazy"
                />
                {points.map(point => (
                    <LookPoint
                        key={`${point.number}-${point.url}`}
                        point={point}
                        isMobile={isMobile}
                        active={activePoint === point}
                        onOpen={p => togglePoint(p, false)}
                    />
                ))}
            </div>
            {isMobile && activePoint && (
                <div className={lookMobileSheet} role="dialog" aria-modal="true">
                    <div className={lookMobileSheetHeader}>
                        <span className={lookMobileSheetNumber}>{activePoint.number}</span>
                        <span className={lookMobileSheetLabel}>
                            {activePoint.label || `Produkt ${activePoint.number}`}
                        </span>
                        <button
                            type="button"
                            className={lookMobileSheetClose}
                            aria-label="Zamknij"
                            onClick={() => setActivePoint(null)}
                        >
                            &times;
                        </button>
                    </div>
                    {activePoint.description && (
                        <p className={lookMobileSheetDesc}>{activePoint.description}</p>
                    )}
                    <a
                        className={lookMobileSheetLink}
                        href={activePoint.url}
                        target="_blank"
                        rel={AFFILIATE_REL}
                    >
                        Zobacz produkt
                    </a>
                </div>
            )}
            <figcaption className={lookLegend}>
                {points.map(point => (
                    <a
                        key={`${point.number}-${point.url}-legend`}
                        className={`${lookLegendItem}${activePoint === point ? ` ${lookLegendItemActive}` : ""}`}
                        href={point.url}
                        target="_blank"
                        rel="nofollow sponsored noopener noreferrer"
                        onClick={
                            isMobile
                                ? (event => {
                                    event.preventDefault();
                                    togglePoint(point, true);
                                })
                                : undefined
                        }
                    >
                        <span className={lookLegendNumber}>{point.number}</span>
                        <span className={lookLegendLabel}>{point.label || "Zobacz produkt"}</span>
                    </a>
                ))}
            </figcaption>
        </figure>
    );
};

const LookHotspotsById: React.FC<{ lookId: number }> = ({ lookId }) => {
    const [look, setLook] = React.useState<ILookApiResponse | null>(null);
    const [status, setStatus] = React.useState<"loading" | "success" | "error">("loading");

    React.useEffect(() => {
        let mounted = true;

        setStatus("loading");

        fetch(config.endpoints.looks.view(lookId))
            .then(response => {
                if (!response.ok) {
                    throw new Error(`Look ${lookId} response ${response.status}`);
                }

                return response.json();
            })
            .then((data: ILookApiResponse) => {
                if (!mounted) {
                    return;
                }

                setLook(data);
                setStatus("success");
            })
            .catch(() => {
                if (!mounted) {
                    return;
                }

                setStatus("error");
            });

        return () => {
            mounted = false;
        };
    }, [lookId]);

    if (status === "loading") {
        return <p className={lookMessage}>Ładuję look...</p>;
    }

    if (status === "error" || !look) {
        return <p className={lookMessage}>Nie udało się pobrać looku #{lookId}.</p>;
    }

    return (
        <LookHotspots
            type="look"
            image={look.imageUrl}
            imageAlt={look.imageAlt || look.name}
            points={look.items || []}
        />
    );
};

const PostContent: React.FC<IPostContentProps> = ({post}) => {
    const { lead, content } = useTranslation(post, translationKeys.post);

    const tags = post.tags;
    const contentBlocks = parseContentBlocks(content);

    return (
        <>
            <Section
                column={'narrow-right'}
                columnTablet={'regular'}
                columnPhone={'regular'}
                className={sectionContent}
            >
                <h2 className={intro}>
                    {lead}
                </h2>
                {contentBlocks.map((block, index) => (
                    block.type === "look" ? (
                        <LookHotspots key={`look-${index}`} {...block} />
                    ) : block.type === "look-id" ? (
                        <LookHotspotsById key={`look-id-${block.lookId}-${index}`} lookId={block.lookId} />
                    ) : (
                        <ReactMarkdown
                            key={`markdown-${index}`}
                            className={cmsContent}
                            // @ts-ignore
                            rehypePlugins={[rehypeRaw]}
                        >
                            {block.content}
                        </ReactMarkdown>
                    )
                ))}
            </Section>
            {tags.length > 0 && (
                <Section
                    column={'regular'}
                    columnTablet={'full'}
                    columnPhone={'full'}
                    className={tagContent}
                >
                    <p className={heading}>Tagi: </p>
                    <ul className={tagsContent}>
                        {tags.map((tag) => (
                            <Tag
                                key={tag.tagId}
                                tag={tag}
                                className={tagClass}
                                before="#"
                            />
                        ))}
                    </ul>
                </Section>
            )}
        </>
    );
};

export default PostContent;
